/**
 * User Preferences Settings Component
 * Allows users to configure their language, timezone, notifications, and geo-tracking preferences
 */

import { useTabbedChrome } from '@/components/weavein';
import { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import type { TextScale, ContrastMode, FocusStyle } from '@/contexts/AccessibilityContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TimezoneSelector } from '@/components/TimezoneSelector';
import { browserTimeZone } from '@/utils/zonedTime';
import { GeoTrackingConsent } from './GeoTrackingConsent';
import { GeoTrackingHistory } from './GeoTrackingHistory';
import { PersonalConnectedServices } from './PersonalConnectedServices';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, Check, Sun, Moon, Monitor, Bell, BellOff, AlertCircle, MapPin, Navigation, CheckCircle2, Link2, Accessibility, Type, Contrast, Eye, MousePointer } from 'lucide-react';
import { toast } from 'sonner';
import { desktopNotifications, NotificationPermissionState } from '@/services/desktopNotifications';
import { DesktopUpdateSettings } from '@/components/desktop/DesktopUpdateSettings';
import { OfflinePanelContent } from '@/components/offline/OfflinePanel';
import { NotificationsPage } from '@/components/notifications/NotificationsPage';
import { EventsIFollow } from '@/components/notifications/EventsIFollow';
import { WebPushSetting } from '@/components/notifications/WebPushSetting';
import { PushMasterSetting } from '@/components/notifications/PushMasterSetting';
import { NotificationSelfTest } from '@/components/notifications/NotificationSelfTest';
import { SwipeSettingsCard } from '@/components/settings/SwipeSettingsCard';
import { DesktopNotificationBlockedHint } from '@/components/notifications/DesktopNotificationBlockedHint';

interface UserPreferencesProps {
  defaultTab?: string;
  onNavigate?: (tab: string) => void;
}

/**
 * Read the outcome an OAuth callback appended to the URL.
 *
 * A mailbox/calendar connect leaves the app entirely, and the backend sends the
 * browser back to /settings/integrations?connected=<provider> on success or
 * ?error=<code>&error_description=<text> when the provider refused consent. Without
 * reading it the user came back to a silent screen and could not tell a successful
 * connect from a refused one (TRUS-517).
 */
function readOAuthOutcome(): { connected?: string; error?: string; description?: string } {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    connected: params.get('connected') || undefined,
    error: params.get('error') || undefined,
    description: params.get('error_description') || undefined,
  };
}

const PROVIDER_LABELS: Record<string, string> = {
  microsoft: 'Microsoft 365 / Outlook',
  google: 'Google',
  zoom: 'Zoom',
  asana: 'Asana',
};

export function UserPreferences({ defaultTab = 'general', onNavigate }: UserPreferencesProps = {}) {
  const tabbed = useTabbedChrome();
  const { t, language: currentLanguage, setLanguage: setContextLanguage, refreshTranslations } = useAppTranslation();
  const { preferences, updatePreferences } = useAuth();
  const { settings: accessibilitySettings, updateSettings: updateAccessibilitySettings } = useAccessibility();

  const [language, setLanguage] = useState(preferences?.language_code || 'en');
  const [timezone, setTimezone] = useState(preferences?.timezone || browserTimeZone());
  const [theme, setTheme] = useState(preferences?.theme || 'light');
  const [desktopNotificationsEnabled, setDesktopNotificationsEnabled] = useState(
    preferences?.desktop_notifications_enabled ?? true
  );
  const [notifyWorkflows, setNotifyWorkflows] = useState(
    preferences?.notify_workflows ?? true
  );
  const [notifyCommunications, setNotifyCommunications] = useState(
    preferences?.notify_communications ?? true
  );
  const [notifyTasks, setNotifyTasks] = useState(preferences?.notify_tasks ?? true);
  const [saving, setSaving] = useState(false);
  const [permissionState, setPermissionState] = useState<NotificationPermissionState>('default');
  const [requestingPermission, setRequestingPermission] = useState(false);

  // Returning from an OAuth connect: open Connected Services rather than General,
  // so the user lands on the card whose state just changed. Read once — Tabs is
  // uncontrolled and only reads defaultValue on the first render.
  const [oauthOutcome] = useState(readOAuthOutcome);
  const initialTab = oauthOutcome.connected || oauthOutcome.error ? 'connected-services' : defaultTab;

  useEffect(() => {
    const { connected, error, description } = oauthOutcome;
    if (!connected && !error) return;

    const providerName = PROVIDER_LABELS[connected || ''] || 'Your account';
    if (connected) {
      toast.success(`${providerName} connected`, {
        description: 'Your mailbox and calendar will start syncing shortly.',
      });
    } else {
      toast.error('Connection not completed', {
        // The provider's own reason is the only actionable text there is — most
        // often that the Microsoft 365 tenant needs an admin to approve the app.
        description:
          description ||
          (error === 'no_code'
            ? 'The provider returned without completing the connection. Please try again.'
            : `The provider reported: ${error}. If this keeps happening, your IT admin may need to approve WeaveIn.`),
        duration: 10000,
      });
    }

    // Clear the params so a refresh doesn't replay the toast.
    const url = new URL(window.location.href);
    ['connected', 'error', 'error_description'].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState(window.history.state, '', url.toString());
  }, [oauthOutcome]);

  useEffect(() => {
    if (preferences) {
      setLanguage(preferences.language_code);
      setTimezone(preferences.timezone || browserTimeZone());
      setTheme(preferences.theme || 'light');
      setDesktopNotificationsEnabled(preferences.desktop_notifications_enabled ?? true);
      setNotifyWorkflows(preferences.notify_workflows ?? true);
      setNotifyCommunications(preferences.notify_communications ?? true);
      setNotifyTasks(preferences.notify_tasks ?? true);
    }

    // Check notification permission state
    if (desktopNotifications.isNotificationSupported()) {
      setPermissionState(desktopNotifications.getPermissionState());
    }
  }, [preferences]);

  // Auto-save preferences when they change
  useEffect(() => {
    if (!preferences) return;

    // Check if any values have changed from saved preferences
    const changed =
      language !== preferences.language_code ||
      timezone !== preferences.timezone ||
      theme !== preferences.theme ||
      desktopNotificationsEnabled !== (preferences.desktop_notifications_enabled ?? true) ||
      notifyWorkflows !== (preferences.notify_workflows ?? true) ||
      notifyCommunications !== (preferences.notify_communications ?? true) ||
      notifyTasks !== (preferences.notify_tasks ?? true);

    if (changed && !saving) {
      // Auto-save after a short delay to batch rapid changes
      const timer = setTimeout(() => {
        handleAutoSave();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    language,
    timezone,
    theme,
    desktopNotificationsEnabled,
    notifyWorkflows,
    notifyCommunications,
    notifyTasks,
  ]);

  const handleRequestPermission = async () => {
    setRequestingPermission(true);
    try {
      const newState = await desktopNotifications.requestPermission();
      setPermissionState(newState);

      if (newState === 'granted') {
        toast.success('Notifications Enabled', {
          description: 'Desktop notifications are now enabled.',
        });

        // Show a test notification
        await desktopNotifications.showNotification({
          title: 'Notifications Enabled!',
          body: 'You will now receive desktop notifications for important updates.',
        });
      } else if (newState === 'denied') {
        toast.error('Notifications Blocked', {
          description:
            'Please enable notifications in your browser settings to receive desktop alerts.',
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Permission Error', {
        description: 'Failed to request notification permission.',
      });
    } finally {
      setRequestingPermission(false);
    }
  };

  const handleTestNotification = async () => {
    const result = await desktopNotifications.showNotification({
      title: 'WeaveIn',
      body: 'Test notification — desktop notifications are working ✓',
    });
    // On macOS the Tauri plugin reports permission as granted and posts directly;
    // the app appears under System Settings → Notifications after the first post.
    toast.success('Test notification sent', {
      description:
        result === null
          ? 'If you don\'t see a banner, enable WeaveIn under System Settings → Notifications.'
          : 'Check the top-right of your screen.',
    });
  };

  const handleAutoSave = async () => {
    setSaving(true);
    try {
      await updatePreferences({
        language_code: language,
        timezone: timezone,
        theme: theme,
        desktop_notifications_enabled: desktopNotificationsEnabled,
        notify_workflows: notifyWorkflows,
        notify_communications: notifyCommunications,
        notify_tasks: notifyTasks,
      });

      toast.success('Preferences Saved', {
        description: 'Your changes have been saved automatically.',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Save Failed', {
        description: 'Failed to save preferences. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="section-spacing">
      <div>
        {!tabbed && <h2 className="text-heading-2">{t('settings.preferences')}</h2>}
        <p className="text-secondary mt-2">
          {t('settings.manage_your_language_timezone_notifications', 'Manage your language, timezone, notifications, and location tracking preferences')}
        </p>
      </div>

      <Separator />

      {/* Main Settings Tabs */}
      <Tabs defaultValue={initialTab} className="w-full">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 mb-6">
          <TabsList className="w-max min-w-full flex flex-nowrap bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <TabsTrigger
              value="general"
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-md transition-all duration-200"
            >
              {t('settings.general', 'General')}
            </TabsTrigger>
            <TabsTrigger
              value="connected-services"
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-md transition-all duration-200"
            >
              <Link2 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('settings.connected_services', 'Connected Services')}</span>
              <span className="sm:hidden">Services</span>
            </TabsTrigger>
            <TabsTrigger
              value="geo-consent"
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-md transition-all duration-200"
            >
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('settings.location_consent', 'Location Consent')}</span>
              <span className="sm:hidden">Location</span>
            </TabsTrigger>
            <TabsTrigger
              value="geo-history"
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-md transition-all duration-200"
            >
              <Navigation className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('settings.tracking_history', 'Tracking History')}</span>
              <span className="sm:hidden">History</span>
            </TabsTrigger>
            <TabsTrigger
              value="accessibility"
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-md transition-all duration-200"
            >
              <Accessibility className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('settings.accessibility', 'Accessibility')}</span>
              <span className="sm:hidden">A11y</span>
            </TabsTrigger>
            <TabsTrigger
              value="notifications"
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-md transition-all duration-200"
            >
              <Bell className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('notifications.title', 'Notifications')}</span>
              <span className="sm:hidden">Notifs</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* General Settings Tab */}
        <TabsContent value="general" className="content-spacing animate-fade-in">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4">{t('settings.language_settings', 'Language Settings')}</CardTitle>
              <CardDescription>
                {t('settings.select_your_preferred_language_for', 'Select your preferred language for the application interface')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing">
              <LanguageSelector value={language} onChange={setLanguage} showLabel={true} />
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4">{t('settings.timezone_settings', 'Timezone Settings')}</CardTitle>
              <CardDescription>
                {t('settings.choose_your_timezone_for_accurate', 'Choose your timezone for accurate date and time displays')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing">
              <TimezoneSelector
                value={timezone}
                onChange={setTimezone}
                showLabel={true}
                showAllTimezones={false}
              />
              <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-100 mt-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">{t('settings.timezone_information', 'Timezone Information')}</p>
                    <p>{t('settings.all_dates_and_times_will', 'All dates and times will be displayed in your selected timezone. The backend stores all times in UTC for consistency.')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4">{t('settings.desktop_notifications', 'Desktop Notifications')}</CardTitle>
              <CardDescription>
                {t('settings.manage_desktop_notification_settings_for', 'Manage desktop notification settings for important updates')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing content-spacing">
              {/* Desktop app updates (desktop only) */}
              <DesktopUpdateSettings />
              {/* Browser push (web only; renders nothing in the desktop app) */}
              <WebPushSetting portal="staff" />
              {/* Send a test notification: push and the in-app check (TRUS-668) */}
              <NotificationSelfTest portal="staff" />
              {/* Desktop app: the OS blocks notifications (desktop only) */}
              <DesktopNotificationBlockedHint />
              {/* Offline & sync (desktop only) */}
              {typeof window !== 'undefined' && '__TAURI__' in window && (
                <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                  <Label className="text-base font-semibold mb-3 block">{t('settings.offline_sync', 'Offline & Sync')}</Label>
                  <OfflinePanelContent />
                </div>
              )}
              {/* Permission Status - Prominent Display */}
              <div>
                <Label className="text-base font-semibold mb-3 block">{t('settings.browser_permission', 'Browser Permission')}</Label>

                {/* Permission Status Card */}
                <div className={`rounded-xl border-2 p-6 transition-all duration-200 ${
                  permissionState === 'granted'
                    ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-300 dark:border-green-700 shadow-sm'
                    : permissionState === 'denied'
                    ? 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 border-red-300 dark:border-red-700 shadow-sm'
                    : 'bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/30 dark:to-amber-900/30 border-yellow-300 dark:border-yellow-700 shadow-sm'
                }`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {permissionState === 'granted' ? (
                        <div className="rounded-full bg-white dark:bg-gray-800 shadow-md p-4 border-2 border-green-400 dark:border-green-600">
                          <Bell className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                      ) : permissionState === 'denied' ? (
                        <div className="rounded-full bg-white dark:bg-gray-800 shadow-md p-4 border-2 border-red-400 dark:border-red-600">
                          <BellOff className="h-8 w-8 text-red-600 dark:text-red-400" />
                        </div>
                      ) : (
                        <div className="rounded-full bg-white dark:bg-gray-800 shadow-md p-4 border-2 border-yellow-400 dark:border-yellow-600">
                          <AlertCircle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <h4 className="text-lg font-bold">
                        {permissionState === 'granted'
                          ? 'Notifications Enabled'
                          : permissionState === 'denied'
                          ? 'Notifications Blocked'
                          : 'Notifications Not Configured'}
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {permissionState === 'granted'
                          ? 'Your browser is configured to show desktop notifications from this application.'
                          : permissionState === 'denied'
                          ? 'Notifications are blocked. Please enable them in your browser settings to receive desktop alerts.'
                          : 'Allow this application to send you desktop notifications for important updates.'}
                      </p>
                      {permissionState !== 'granted' && (
                        <div className="pt-2">
                          <Button
                            onClick={handleRequestPermission}
                            disabled={requestingPermission || permissionState === 'denied'}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md"
                            size="lg"
                          >
                            {requestingPermission ? (
                              <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                {t('settings.requesting_permission', 'Requesting Permission...')}
                              </>
                            ) : (
                              <>
                                <Bell className="mr-2 h-5 w-5" />
                                {t('settings.enable_notifications', 'Enable Notifications')}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      <div className="pt-2">
                        <Button variant="outline" size="sm" onClick={handleTestNotification}>
                          <Bell className="mr-2 h-4 w-4" />
                          {t('settings.send_test_notification', 'Send test notification')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Notifications Master Toggle */}
              {permissionState === 'granted' && (
                <div>
                  <Label className="text-base font-semibold mb-3 block">{t('settings.notification_settings', 'Notification Settings')}</Label>
                  <button
                    type="button"
                    onClick={() => setDesktopNotificationsEnabled(!desktopNotificationsEnabled)}
                    className={`w-full rounded-xl border-2 p-6 transition-all duration-200 text-left ${
                      desktopNotificationsEnabled
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-300 dark:border-blue-700 shadow-md'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id="desktop-notifications"
                        checked={desktopNotificationsEnabled}
                        onCheckedChange={setDesktopNotificationsEnabled}
                        className="h-6 w-6 mt-0.5 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`rounded-full p-3 transition-colors ${
                          desktopNotificationsEnabled ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          <Bell className={`h-6 w-6 ${desktopNotificationsEnabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="desktop-notifications" className="text-base font-semibold cursor-pointer block">
                            {t('settings.enable_desktop_notifications', 'Enable Desktop Notifications')}
                          </Label>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('settings.show_browser_notifications_for_important', 'Show browser notifications for important updates and events')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Notification Type Preferences */}
              {desktopNotificationsEnabled && permissionState === 'granted' && (
                <div>
                  <Label className="text-base font-semibold mb-3 block">{t('settings.notification_types', 'Notification Types')}</Label>
                  <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                    <div className="p-6 space-y-3">
                      {/* Workflows */}
                      <button
                        type="button"
                        onClick={() => setNotifyWorkflows(!notifyWorkflows)}
                        className={`w-full flex items-start gap-4 p-4 rounded-xl transition-all duration-200 text-left ${
                          notifyWorkflows
                            ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border-2 border-purple-300 dark:border-purple-700 shadow-sm'
                            : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                        }`}
                      >
                        <Checkbox
                          id="notify-workflows"
                          checked={notifyWorkflows}
                          onCheckedChange={setNotifyWorkflows}
                          className="h-6 w-6 mt-0.5 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`rounded-full p-2 transition-colors ${
                            notifyWorkflows ? 'bg-purple-100 dark:bg-purple-900/50' : 'bg-gray-100 dark:bg-gray-600'
                          }`}>
                            <CheckCircle2 className={`h-5 w-5 ${notifyWorkflows ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label htmlFor="notify-workflows" className="text-base font-semibold cursor-pointer block">
                              {t('settings.workflow_completions', 'Workflow Completions')}
                            </Label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {t('settings.get_notified_when_a_workflow', 'Get notified when a workflow is completed')}
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Communications */}
                      <button
                        type="button"
                        onClick={() => setNotifyCommunications(!notifyCommunications)}
                        className={`w-full flex items-start gap-4 p-4 rounded-xl transition-all duration-200 text-left ${
                          notifyCommunications
                            ? 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 border-2 border-blue-300 dark:border-blue-700 shadow-sm'
                            : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                        }`}
                      >
                        <Checkbox
                          id="notify-communications"
                          checked={notifyCommunications}
                          onCheckedChange={setNotifyCommunications}
                          className="h-6 w-6 mt-0.5 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`rounded-full p-2 transition-colors ${
                            notifyCommunications ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-600'
                          }`}>
                            <CheckCircle2 className={`h-5 w-5 ${notifyCommunications ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label htmlFor="notify-communications" className="text-base font-semibold cursor-pointer block">
                              {t('settings.inbound_communications', 'Inbound Communications')}
                            </Label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {t('settings.get_notified_about_new_messages', 'Get notified about new messages, emails, and calls')}
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Tasks */}
                      <button
                        type="button"
                        onClick={() => setNotifyTasks(!notifyTasks)}
                        className={`w-full flex items-start gap-4 p-4 rounded-xl transition-all duration-200 text-left ${
                          notifyTasks
                            ? 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 border-2 border-emerald-300 dark:border-emerald-700 shadow-sm'
                            : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                        }`}
                      >
                        <Checkbox
                          id="notify-tasks"
                          checked={notifyTasks}
                          onCheckedChange={setNotifyTasks}
                          className="h-6 w-6 mt-0.5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`rounded-full p-2 transition-colors ${
                            notifyTasks ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-gray-100 dark:bg-gray-600'
                          }`}>
                            <CheckCircle2 className={`h-5 w-5 ${notifyTasks ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label htmlFor="notify-tasks" className="text-base font-semibold cursor-pointer block">
                              {t('settings.task_assignments', 'Task Assignments')}
                            </Label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {t('settings.get_notified_when_youre_assigned', 'Get notified when you\'re assigned a new task')}
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!desktopNotifications.isNotificationSupported() && (
                <div className="rounded-xl bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/30 dark:to-amber-900/30 border-2 border-yellow-300 dark:border-yellow-700 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">{t('settings.browser_not_supported', 'Browser Not Supported')}</p>
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        {t('settings.desktop_notifications_are_not_supported', 'Desktop notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge to enable desktop notifications.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* TRUS-761: what swiping a row does, sensitivity, trackpad */}
          <SwipeSettingsCard />

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4">{t('settings.theme_settings', 'Theme Settings')}</CardTitle>
              <CardDescription>
                {t('settings.choose_your_preferred_color_theme', 'Choose your preferred color theme for the application')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing">
              <div className="space-y-3">
                <Label htmlFor="theme-select" className="text-base font-semibold">{t('settings.theme', 'Theme')}</Label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'light', icon: Sun, label: 'Light', gradient: 'from-yellow-50 to-amber-50', border: 'yellow' },
                    { value: 'dark', icon: Moon, label: 'Dark', gradient: 'from-indigo-50 to-purple-50', border: 'indigo' },
                    { value: 'system', icon: Monitor, label: 'System', gradient: 'from-gray-50 to-slate-50', border: 'gray' }
                  ].map((themeOption) => (
                    <button
                      key={themeOption.value}
                      type="button"
                      onClick={() => setTheme(themeOption.value)}
                      className={`relative flex flex-col items-center gap-3 rounded-xl border-2 p-6 transition-all duration-200 ${
                        theme === themeOption.value
                          ? `bg-gradient-to-r ${themeOption.gradient} dark:from-gray-700 dark:to-gray-800 border-${themeOption.border}-400 dark:border-${themeOption.border}-600 shadow-lg scale-105`
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                      }`}
                    >
                      {theme === themeOption.value && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle2 className={`h-6 w-6 text-${themeOption.border}-600`} />
                        </div>
                      )}
                      <div className={`rounded-full p-3 ${theme === themeOption.value ? `bg-${themeOption.border}-100 dark:bg-${themeOption.border}-900/50` : 'bg-gray-100 dark:bg-gray-700'}`}>
                        <themeOption.icon className={`h-8 w-8 ${theme === themeOption.value ? `text-${themeOption.border}-600 dark:text-${themeOption.border}-400` : 'text-gray-400 dark:text-gray-500'}`} />
                      </div>
                      <span className={`text-base font-semibold ${theme === themeOption.value ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                        {themeOption.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-100 mt-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p><strong>{t('settings.light', 'Light:')}</strong> {t('settings.always_use_light_mode', 'Always use light mode.')}</p>
                    <p><strong>{t('settings.dark', 'Dark:')}</strong> {t('settings.always_use_dark_mode', 'Always use dark mode.')}</p>
                    <p><strong>{t('settings.system', 'System:')}</strong> {t('settings.automatically_match_your_operating_systems', 'Automatically match your operating system\'s theme preference.')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Auto-save indicator */}
          {saving && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('settings.saving_preferences', 'Saving preferences...')}</span>
            </div>
          )}
        </TabsContent>

        {/* Connected Services Tab */}
        <TabsContent value="connected-services" className="animate-fade-in">
          <PersonalConnectedServices />
        </TabsContent>

        {/* Geo-Tracking Consent Tab */}
        <TabsContent value="geo-consent" className="animate-fade-in">
          <GeoTrackingConsent />
        </TabsContent>

        {/* Geo-Tracking History Tab */}
        <TabsContent value="geo-history" className="animate-fade-in">
          <GeoTrackingHistory />
        </TabsContent>

        {/* Accessibility Settings Tab */}
        <TabsContent value="accessibility" className="content-spacing animate-fade-in">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4 flex items-center gap-2">
                <Type className="h-5 w-5" />
                {t('settings.text_size', 'Text Size')}
              </CardTitle>
              <CardDescription>
                {t('settings.adjust_text_size_for_better_readability', 'Adjust text size for better readability')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">A</span>
                  <div className="flex-1 mx-4">
                    <div className="grid grid-cols-5 gap-2">
                      {(['100', '125', '150', '175', '200'] as TextScale[]).map((scale) => (
                        <button
                          key={scale}
                          onClick={() => updateAccessibilitySettings({ textScale: scale })}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            accessibilitySettings.textScale === scale
                              ? 'bg-primary text-primary-foreground shadow-md'
                              : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {scale}%
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="text-lg font-bold">A</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('settings.current_text_scale', 'Current: {{scale}}%', { scale: accessibilitySettings.textScale })}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4 flex items-center gap-2">
                <Contrast className="h-5 w-5" />
                {t('settings.contrast_mode', 'Contrast Mode')}
              </CardTitle>
              <CardDescription>
                {t('settings.adjust_contrast_for_visual_clarity', 'Adjust contrast for better visual clarity')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'normal' as ContrastMode, label: t('settings.normal', 'Normal'), desc: t('settings.standard_contrast', 'Standard contrast') },
                  { value: 'high' as ContrastMode, label: t('settings.high', 'High'), desc: t('settings.increased_contrast', 'Increased contrast') },
                  { value: 'highest' as ContrastMode, label: t('settings.highest', 'Highest'), desc: t('settings.maximum_contrast', 'Maximum contrast') },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateAccessibilitySettings({ contrastMode: option.value })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      accessibilitySettings.contrastMode === option.value
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4 flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {t('settings.visual_preferences', 'Visual Preferences')}
              </CardTitle>
              <CardDescription>
                {t('settings.customize_visual_experience', 'Customize your visual experience')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div>
                  <Label className="font-semibold">{t('settings.reduce_motion', 'Reduce Motion')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.minimize_animations_and_transitions', 'Minimize animations and transitions')}
                  </p>
                </div>
                <Switch
                  checked={accessibilitySettings.reduceMotion}
                  onCheckedChange={(checked) => updateAccessibilitySettings({ reduceMotion: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div>
                  <Label className="font-semibold">{t('settings.reduce_transparency', 'Reduce Transparency')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.reduce_transparent_backgrounds', 'Reduce transparent backgrounds')}
                  </p>
                </div>
                <Switch
                  checked={accessibilitySettings.reduceTransparency}
                  onCheckedChange={(checked) => updateAccessibilitySettings({ reduceTransparency: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div>
                  <Label className="font-semibold">{t('settings.dyslexia_friendly_font', 'Dyslexia-Friendly Font')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.use_opendyslexic_font', 'Use OpenDyslexic font for better readability')}
                  </p>
                </div>
                <Switch
                  checked={accessibilitySettings.dyslexiaFriendlyFont}
                  onCheckedChange={(checked) => updateAccessibilitySettings({ dyslexiaFriendlyFont: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div>
                  <Label className="font-semibold">{t('settings.show_link_underlines', 'Show Link Underlines')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.always_underline_links', 'Always underline links for visibility')}
                  </p>
                </div>
                <Switch
                  checked={accessibilitySettings.showLinkUnderlines}
                  onCheckedChange={(checked) => updateAccessibilitySettings({ showLinkUnderlines: checked })}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4 flex items-center gap-2">
                <MousePointer className="h-5 w-5" />
                {t('settings.interaction_settings', 'Interaction Settings')}
              </CardTitle>
              <CardDescription>
                {t('settings.adjust_interaction_preferences', 'Adjust how you interact with the application')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing space-y-4">
              <div>
                <Label className="font-semibold mb-3 block">{t('settings.focus_indicator_style', 'Focus Indicator Style')}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'default' as FocusStyle, label: t('settings.default', 'Default') },
                    { value: 'enhanced' as FocusStyle, label: t('settings.enhanced', 'Enhanced') },
                    { value: 'high-visibility' as FocusStyle, label: t('settings.high_visibility', 'High Visibility') },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateAccessibilitySettings({ focusStyle: option.value })}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        accessibilitySettings.focusStyle === option.value
                          ? 'border-primary bg-primary/5 font-semibold'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="font-semibold mb-3 block">{t('settings.touch_target_size', 'Touch Target Size')}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'normal' as const, label: t('settings.normal', 'Normal'), desc: '44px' },
                    { value: 'large' as const, label: t('settings.large', 'Large'), desc: '48px' },
                    { value: 'extra-large' as const, label: t('settings.extra_large', 'Extra Large'), desc: '56px' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateAccessibilitySettings({ targetSize: option.value })}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        accessibilitySettings.targetSize === option.value
                          ? 'border-primary bg-primary/5 font-semibold'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div>{option.label}</div>
                      <div className="text-xs text-muted-foreground">{option.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div>
                  <Label className="font-semibold">{t('settings.keyboard_shortcuts', 'Keyboard Shortcuts')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.enable_keyboard_shortcuts', 'Enable keyboard shortcuts for navigation')}
                  </p>
                </div>
                <Switch
                  checked={accessibilitySettings.keyboardShortcutsEnabled}
                  onCheckedChange={(checked) => updateAccessibilitySettings({ keyboardShortcutsEnabled: checked })}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-heading-4">{t('settings.reading_aids', 'Reading Aids')}</CardTitle>
              <CardDescription>
                {t('settings.improve_text_readability', 'Improve text readability with spacing options')}
              </CardDescription>
            </CardHeader>
            <CardContent className="card-spacing space-y-4">
              <div>
                <Label className="font-semibold mb-3 block">{t('settings.line_spacing', 'Line Spacing')}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'normal' as const, label: t('settings.normal', 'Normal') },
                    { value: 'relaxed' as const, label: t('settings.relaxed', 'Relaxed') },
                    { value: 'loose' as const, label: t('settings.loose', 'Loose') },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateAccessibilitySettings({ lineSpacing: option.value })}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        accessibilitySettings.lineSpacing === option.value
                          ? 'border-primary bg-primary/5 font-semibold'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="font-semibold mb-3 block">{t('settings.letter_spacing', 'Letter Spacing')}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'normal' as const, label: t('settings.normal', 'Normal') },
                    { value: 'wide' as const, label: t('settings.wide', 'Wide') },
                    { value: 'wider' as const, label: t('settings.wider', 'Wider') },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateAccessibilitySettings({ letterSpacing: option.value })}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        accessibilitySettings.letterSpacing === option.value
                          ? 'border-primary bg-primary/5 font-semibold'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  {t('settings.accessibility_note_title', 'Accessibility Settings')}
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('settings.accessibility_note_description', 'These settings are saved locally to your browser and will persist across sessions. They comply with WCAG 2.1 AA accessibility guidelines.')}
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="animate-fade-in">
          {/* TRUS-697: the one switch for push on every device (phones and browsers). */}
          <PushMasterSetting />
          {/* TRUS-685: the events this person follows (bell / push). */}
          <EventsIFollow />
          <NotificationsPage onNavigate={onNavigate} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
